import { ASSERTION_TYPES, COLLECTION_TOPICS, assertionHelp } from "@constants/agentSim";
import { emptyAssertion, changeAssertionType, paramKindFor } from "./assertionUtils";
import Button from "@components/ui/Button";
import Input from "@components/ui/Input";

const selectClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none";

export default function AssertionEditor({ value = [], onChange }) {
  const update = (idx, next) => onChange(value.map((a, i) => (i === idx ? next : a)));
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const add = () => onChange([...value, emptyAssertion()]);

  const toggleTopic = (idx, topic) => {
    const cur = value[idx].params.topics || [];
    const topics = cur.includes(topic) ? cur.filter((t) => t !== topic) : [...cur, topic];
    update(idx, { ...value[idx], params: { topics } });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Assertions are the checks that decide whether this scenario passes. Every
        assertion must pass for the test to pass. Pick a check type, set its
        parameters, then add as many as you need.
      </p>

      {value.length === 0 && (
        <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg p-3">
          No assertions yet — click <span className="font-medium">“+ Add assertion”</span> to define what must be true for this scenario to pass.
        </div>
      )}

      {value.map((a, idx) => {
        const kind = paramKindFor(a.type);
        return (
          <div key={idx} className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <select
                aria-label={`assertion-type-${idx}`}
                className={selectClass}
                value={a.type}
                onChange={(e) => update(idx, changeAssertionType(a, e.target.value))}
              >
                {ASSERTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <button
                type="button"
                aria-label={`remove assertion ${idx}`}
                className="text-gray-500 hover:text-red-600 px-2"
                onClick={() => remove(idx)}
              >✕</button>
            </div>

            {assertionHelp(a.type) && (
              <p className="text-xs text-gray-500">{assertionHelp(a.type)}</p>
            )}

            {kind === "topics" && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-gray-600">Topics that must be collected:</div>
                <div className="flex flex-wrap gap-2">
                  {COLLECTION_TOPICS.map((t) => (
                    <label key={t} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        aria-label={`topic-${idx}-${t}`}
                        checked={(a.params.topics || []).includes(t)}
                        onChange={() => toggleTopic(idx, t)}
                      />
                      {t}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {kind === "topic" && (
              <select
                aria-label={`assertion-topic-${idx}`}
                className={selectClass}
                value={a.params.topic || ""}
                onChange={(e) => update(idx, { ...a, params: { topic: e.target.value } })}
              >
                <option value="">— select topic —</option>
                {COLLECTION_TOPICS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}

            <Input
              label="Description (optional)"
              value={a.description || ""}
              onChange={(e) => update(idx, { ...a, description: e.target.value })}
            />
          </div>
        );
      })}
      <Button color="light" onClick={add}>+ Add assertion</Button>
    </div>
  );
}
